import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Zap, ChevronDown } from "lucide-react";

const NAV = [
  { to: "/academy", label: "Academy" },
  { to: "/content-agency", label: "Career Ecosystem" },
  { to: "/perforange-os", label: "Perforange OS" },
  { to: "/principals-corner", label: "Principal's Corner" },
  { to: "/partnership", label: "Partnership" },
];

const RESOURCES = [
  { to: "/blog", label: "Blog", desc: "Insights, faculty notes, and founder's journals." },
  { to: "/about", label: "About Us", desc: "Mission, leadership, compliance architecture." },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const [resMobileOpen, setResMobileOpen] = useState(false);
  const resRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setResOpen(false);
    setResMobileOpen(false);
  }, [location.pathname]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (resRef.current && !resRef.current.contains(e.target)) setResOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const resourcesActive = RESOURCES.some((r) => location.pathname.startsWith(r.to));

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0C]/85 backdrop-blur-xl border-b border-white/8"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-8">
        {/* Left: nav (desktop) */}
        <nav className="hidden md:flex items-center gap-3 lg:gap-5 xl:gap-7 order-2 md:order-1" data-testid="primary-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-xs md:text-[13px] lg:text-sm font-semibold tracking-wide whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-[#FF8C00]"
                    : "text-[#A1A1AA] hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* Resources dropdown */}
          <div className="relative" ref={resRef} data-testid="resources-dropdown">
            <button
              type="button"
              onClick={() => setResOpen((v) => !v)}
              data-testid="nav-resources-trigger"
              className={`inline-flex items-center gap-1 text-xs md:text-[13px] lg:text-sm font-semibold tracking-wide whitespace-nowrap transition-colors ${
                resourcesActive || resOpen ? "text-[#FF8C00]" : "text-[#A1A1AA] hover:text-white"
              }`}
              aria-haspopup="true"
              aria-expanded={resOpen}
            >
              Resources
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${resOpen ? "rotate-180" : ""}`} />
            </button>

            {resOpen && (
              <div
                role="menu"
                data-testid="resources-menu"
                className="absolute right-0 mt-3 w-72 rounded-xl border border-white/10 bg-[#0C0C10]/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-2"
              >
                {RESOURCES.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    data-testid={`nav-resources-${r.to.replace("/", "")}`}
                    onClick={() => setResOpen(false)}
                    className="block px-4 py-3 rounded-lg hover:bg-white/5 transition-colors group"
                  >
                    <p className="text-sm font-bold text-white group-hover:text-[#FF8C00] transition-colors">{r.label}</p>
                    <p className="text-xs text-[#A1A1AA] mt-0.5">{r.desc}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right: Brand + CTA */}
        <div className="flex items-center gap-3 lg:gap-4 order-1 md:order-2 ml-auto">
          <Link
            to="/"
            data-testid="brand-logo"
            className="flex items-center gap-2 group"
          >
            <span className="relative flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-br from-[#FF8C00] to-[#FF5A00] shadow-[0_0_24px_rgba(255,140,0,0.45)]">
              <Zap className="w-5 h-5 text-[#0A0A0C]" strokeWidth={3} />
            </span>
            <span className="font-display font-black text-xl tracking-tight text-white">
              perfo<span className="text-[#FF8C00]">range</span>
            </span>
          </Link>
          <Link
            to="/contact"
            data-testid="header-cta-empanel"
            className="hidden md:inline-flex items-center px-4 py-2 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-sm font-bold hover:bg-[#FFA333] transition-colors"
          >
            Get Empaneled
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/8 bg-[#0A0A0C]/95 backdrop-blur-xl" data-testid="mobile-nav">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-sm font-semibold ${
                    isActive
                      ? "bg-[#FF8C00]/10 text-[#FF8C00]"
                      : "text-[#D4D4D8] hover:bg-white/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            {/* Mobile Resources accordion */}
            <button
              type="button"
              onClick={() => setResMobileOpen((v) => !v)}
              data-testid="mobile-nav-resources-trigger"
              className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold ${
                resourcesActive ? "bg-[#FF8C00]/10 text-[#FF8C00]" : "text-[#D4D4D8] hover:bg-white/5"
              }`}
            >
              Resources
              <ChevronDown className={`w-4 h-4 transition-transform ${resMobileOpen ? "rotate-180" : ""}`} />
            </button>
            {resMobileOpen && (
              <div className="pl-3 mt-1 mb-1 space-y-1">
                {RESOURCES.map((r) => (
                  <NavLink
                    key={r.to}
                    to={r.to}
                    data-testid={`mobile-nav-resources-${r.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `block px-3 py-2.5 rounded-lg text-sm ${
                        isActive ? "text-[#FF8C00]" : "text-[#A1A1AA] hover:text-white"
                      }`
                    }
                  >
                    {r.label}
                  </NavLink>
                ))}
              </div>
            )}

            <Link
              to="/contact"
              data-testid="mobile-header-cta"
              className="mt-2 px-3 py-3 rounded-lg bg-[#FF8C00] text-[#0A0A0C] text-center text-sm font-bold"
            >
              Get Empaneled
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
